import { createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "universal-cookie";
import GetProxy from "../app/GetProxy";
import { handleResponseCode } from "../app/utilities/helpers";

const proxy = GetProxy();
const cookies = new Cookies();

// Helper function to normalize URL by removing duplicate slashes
const normalizeUrl = (url) => {
  return url.replace(/([^:]\/)\/+/g, "$1");
};

// Generic API Request Wrapper

export const apiRequest = async ({
  endpoint,
  payload,
  method = "POST",
  responseType = "json",
  acceptHeader = "application/json"
}) => {
  const apiToken = cookies.get("apitoken");
  const username = cookies.get("username");

  try {
    const normalizedUrl = normalizeUrl(`${proxy}${endpoint}`);
    const response = await fetch(normalizedUrl, {
      method,
      headers: {
        Accept: acceptHeader,
        "Content-Type": "application/json",
        "DTOP-API-TOKEN": apiToken,
        USERNAME: username,
      },
      body: method === "POST" ? JSON.stringify(payload || {}) : undefined,
    });

    handleResponseCode(response);

    let data;
    if (responseType === "blob") {
      data = await response.blob();
    } else {
      data = await response.json();
    }

    return response.ok ? data : Promise.reject(data);
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

// Dynamic Thunk

export const createDynamicThunk = (
  name,
  defaultEndpoint,
  paramKeys = [],
  method = "POST",
  config = {}
) =>
  createAsyncThunk(name, async (props = {}) => {
    const { payload = {}, endpoint, ...rest } = props;

    // If endpoint passed, use it, else fallback to default
    let finalEndpoint = endpoint
      ? `${defaultEndpoint}${endpoint}`
      : defaultEndpoint;

    if (paramKeys.length > 0) {
      // filter out "endpoint" because that's not a path param
      const pathKeys = paramKeys.filter((k) => k !== "endpoint");
      if (pathKeys.length > 0) {
        const pathParts = pathKeys.map((key) => rest[key]);
        finalEndpoint = `${finalEndpoint}/${pathParts.join("/")}`;
      }
    }

    // Normalize the final endpoint to remove duplicate slashes
    finalEndpoint = normalizeUrl(finalEndpoint);

    return await apiRequest({
      endpoint: finalEndpoint,
      payload,
      method,
      ...config,
    });
  });

//  Reducer Case Helper

export const addApiCases = (builder, thunk, stateKey, isDelete = false) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[`${stateKey}Loading`] = true;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[`${stateKey}Loading`] = false;
      if (isDelete) {
        state[stateKey] = action.payload;
      } else {
        state[stateKey] = action.payload ?? [];
      }
    })
    .addCase(thunk.rejected, (state) => {
      state[`${stateKey}Loading`] = false;
      if (isDelete) {
        state.errorDelete = "Something went wrong";
      } else {
        state[stateKey] = [];
      }
    });
};

export const addApiCasesTable = (
  builder,
  thunk,
  dataKey,
  countKey,
  dataPropName = "data",
  countPropName = "totalElements"
) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[`${dataKey}Loading`] = true;
      state[`${countKey}Loading`] = true;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[`${dataKey}Loading`] = false;
      state[`${countKey}Loading`] = false;

      state[dataKey] = action.payload?.[dataPropName] ?? [];
      state[countKey] = action.payload?.[countPropName] ?? 0;
    })
    .addCase(thunk.rejected, (state) => {
      state[`${dataKey}Loading`] = false;
      state[`${countKey}Loading`] = false;

      state[dataKey] = [];
      state[countKey] = 0;
    });
};
